import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PageHeader, euro, fmtDate, assetTypeLabel } from "@/components/shared";
import {
  Truck, CheckCircle2, ClipboardSignature, Wrench, Tags, Users2,
  AlertTriangle, Euro, PackagePlus, ChevronRight, Loader2,
} from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";

function StatCard({ label, value, icon: Icon, accent, onClick }) {
  return (
    <Card
      onClick={onClick}
      data-testid={`stat-${label.toLowerCase().replace(/[\s.]/g, "-")}`}
      className="p-6 rounded-lg border hover:border-primary/60 hover:shadow-sm transition-all cursor-pointer group"
    >
      <div className="flex items-start justify-between">
        <div className="label-caps text-muted-foreground">{label}</div>
        <div className={`flex items-center justify-center h-9 w-9 rounded-md ${accent}`}>
          <Icon className="h-5 w-5" strokeWidth={2} />
        </div>
      </div>
      <div className="flex items-end justify-between mt-4">
        <div className="stat-number text-4xl">{value}</div>
        <ChevronRight className="h-5 w-5 text-muted-foreground/30 group-hover:text-primary transition-colors" />
      </div>
    </Card>
  );
}

const today = () => new Date().toISOString().slice(0, 10);
const yearsAgo = (n) => { const d = new Date(); d.setFullYear(d.getFullYear() - n); return d.toISOString().slice(0, 10); };

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [from, setFrom] = useState(yearsAgo(1));
  const [to, setTo] = useState(today());
  const [detail, setDetail] = useState(null); // {metric, title}
  const [detailItems, setDetailItems] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.get("/dashboard/stats", { params: { from, to } }).then(({ data }) => setStats(data));
    api.get("/maintenance/alerts").then(({ data }) => setAlerts(data));
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const openDetail = (metric, title) => {
    setDetail({ metric, title });
    setDetailItems(null);
    api.get("/dashboard/detail", { params: { metric, from, to } }).then(({ data }) => setDetailItems(data.items));
  };

  if (!stats) return null;

  const pieColors = ["hsl(232 51% 31%)", "hsl(32 95% 50%)", "hsl(152 60% 40%)", "hsl(280 55% 50%)", "hsl(340 70% 50%)", "hsl(200 70% 45%)"];
  const pieData = Object.entries(stats.types || {})
    .map(([name, value], i) => ({ name: assetTypeLabel(name), value, color: pieColors[i % pieColors.length] }))
    .filter((d) => d.value > 0);

  const tiles = [
    { label: "Beni totali", value: stats.total_assets, icon: Truck, accent: "bg-primary/10 text-primary", metric: "total_assets" },
    { label: "Disponibili", value: stats.available, icon: CheckCircle2, accent: "bg-success/12 text-success", metric: "available" },
    { label: "Assegnati", value: stats.assigned, icon: ClipboardSignature, accent: "bg-primary/10 text-primary", metric: "assigned" },
    { label: "In manutenzione", value: stats.in_maintenance, icon: Wrench, accent: "bg-warning/12 text-warning", metric: "in_maintenance" },
    { label: "Beni censiti", value: stats.new_assets, icon: PackagePlus, accent: "bg-primary/10 text-primary", metric: "new_assets" },
    { label: "Assegnazioni", value: stats.new_assignments, icon: ClipboardSignature, accent: "bg-secondary text-foreground", metric: "new_assignments" },
    { label: "Interventi", value: stats.maintenance_count, icon: Wrench, accent: "bg-secondary text-foreground", metric: "maintenance_count" },
    { label: "Spesa manut.", value: euro(stats.maintenance_spend), icon: Euro, accent: "bg-secondary text-foreground", metric: "maintenance_spend" },
    { label: "Avvisi scadenze", value: stats.alerts_count, icon: AlertTriangle, accent: "bg-destructive/10 text-destructive", metric: "alerts_count" },
    { label: "Categorie", value: stats.categories, icon: Tags, accent: "bg-secondary text-foreground", metric: "categories" },
    { label: "Dipendenti", value: stats.employees, icon: Users2, accent: "bg-secondary text-foreground", metric: "employees" },
  ];

  return (
    <div>
      <PageHeader eyebrow="Panoramica" title="Dashboard" subtitle="Situazione asset aziendali · dati per periodo">
        <div className="flex items-end gap-2 bg-card border border-border rounded-md p-2">
          <div>
            <div className="label-caps text-[0.6rem] text-muted-foreground mb-1 px-1">Da</div>
            <Input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} data-testid="dashboard-from" className="h-9 w-[150px]" />
          </div>
          <div>
            <div className="label-caps text-[0.6rem] text-muted-foreground mb-1 px-1">A</div>
            <Input type="date" value={to} min={from} max={today()} onChange={(e) => setTo(e.target.value)} data-testid="dashboard-to" className="h-9 w-[150px]" />
          </div>
        </div>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {tiles.map((t) => (
          <StatCard key={t.label} {...t} onClick={() => openDetail(t.metric, t.label)} />
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mt-6">
        <Card className="p-6 rounded-lg lg:col-span-1">
          <div className="label-caps text-muted-foreground mb-4">Ripartizione per tipo</div>
          {pieData.length ? (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}>
                  {pieData.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-sm text-muted-foreground py-12 text-center">Nessun bene censito</div>
          )}
        </Card>

        <Card className="p-6 rounded-lg lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div className="label-caps text-muted-foreground">Prossime scadenze</div>
            <AlertTriangle className="h-4 w-4 text-warning" />
          </div>
          {alerts.length ? (
            <div className="space-y-2">
              {alerts.slice(0, 6).map((a, i) => (
                <div key={`${a.asset_id}-${a.basis}-${i}`} data-testid={`alert-${a.asset_id}`}
                  onClick={() => navigate(`/beni/${a.asset_id}`)}
                  className="flex items-center justify-between p-3 rounded-md border border-border hover:border-primary/50 cursor-pointer transition-colors">
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{a.asset_name} <span className="text-muted-foreground font-normal">· {a.activity_type_name}</span></div>
                    <div className="text-xs text-muted-foreground truncate">{a.detail}</div>
                  </div>
                  <span className={`label-caps px-2.5 py-1 rounded-md shrink-0 ml-2 ${a.status === "scaduta" ? "bg-destructive/12 text-destructive" : "bg-warning/12 text-warning"}`}>
                    {a.urgency}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <CheckCircle2 className="h-10 w-10 text-success/60 mb-2" />
              <span className="text-sm">Nessuna scadenza imminente</span>
            </div>
          )}
        </Card>
      </div>

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{detail?.title}</DialogTitle>
            <DialogDescription>Dettaglio del dato aggregato · periodo {fmtDate(from)} — {fmtDate(to)}</DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto -mx-2 px-2" data-testid="detail-list">
            {detailItems === null ? (
              <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
            ) : detailItems.length === 0 ? (
              <div className="text-sm text-muted-foreground text-center py-10">Nessun dato per il periodo selezionato.</div>
            ) : (
              <div className="space-y-2">
                {detailItems.map((it) => (
                  <div key={it.id} onClick={() => { if (it.link) { setDetail(null); navigate(it.link); } }}
                    className="flex items-center justify-between p-3 rounded-md border border-border hover:border-primary/50 cursor-pointer">
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{it.primary}</div>
                      <div className="text-xs text-muted-foreground truncate">{it.secondary}</div>
                    </div>
                    {it.meta && <span className="text-sm font-semibold shrink-0 ml-3">{it.meta}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
